/**
 * Custom hook for fetching and managing Basecamp todo data
 * Extracted from BasecampTodoViewer for better separation of concerns
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { basecampApi } from '@/lib/basecamp-api';
import { isOfflineOrUnreachableFetchError } from '@/lib/api';
import type { BasecampTodo } from '@/types/basecamp';
import { toast } from 'sonner';
import { resolveBasecampTodoIngestUrlFromTodo } from '@/components/layout/document-viewer.utils';

interface RelatedDocument {
  id: string;
  title: string;
  type: string;
  status: string;
  excerpt: string;
  tags: string[];
  projectName: string;
  updatedBy: string;
  updatedAt: string;
  filePath: string;
  matchScore: number;
  primary?: boolean;
  documentIndex?: number | null;
  documentRole?: string | null;
}

interface UseBasecampTodoDataProps {
  todoId: string;
  projectId?: string;
  projectName?: string;
  onTodoLoad?: (todo: BasecampTodo) => void;
  /** When false, comments load only when loadComments() is called explicitly */
  autoLoadComments?: boolean;
}

export function useBasecampTodoData({
  todoId,
  projectId,
  projectName: propProjectName,
  onTodoLoad,
  autoLoadComments = false,
}: UseBasecampTodoDataProps) {
  const [todo, setTodo] = useState<BasecampTodo | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [relatedDocs, setRelatedDocs] = useState<RelatedDocument[]>([]);
  const [connections, setConnections] = useState<{
    semantic: any[];
    casual: any[];
    todos: import('@/lib/basecamp-api').RelatedTodo[];
  }>({
    semantic: [],
    casual: [],
    todos: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  /** True after a comments fetch finishes (success or empty). Prevents re-fetch loops when count is 0. */
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoadFailed, setCommentsLoadFailed] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(propProjectName || null);
  const [isBookmarked, setIsBookmarked] = useState<boolean | null>(null);
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
  const [isPostingComment, setIsPostingComment] = useState(false);
  const loadingRef = useRef<string | null>(null);

  // Fetch project name if not provided
  useEffect(() => {
    if (propProjectName) {
      setProjectName(propProjectName);
      return;
    }

    if (!projectId) return;

    const fetchProjectName = async () => {
      try {
        const projects = await basecampApi.getProjects();
        const project = projects.find(p => p.id?.toString() === projectId?.toString());
        if (project) {
          setProjectName(project.name);
        }
      } catch (err) {
        console.warn('[useBasecampTodoData] Could not fetch project name:', err);
      }
    };

    fetchProjectName();
  }, [projectId, propProjectName]);

  // Reset comments when navigating to a different todo
  useEffect(() => {
    setComments([]);
    setCommentsLoaded(false);
    setCommentsLoadFailed(false);
  }, [todoId, projectId]);

  // Main data loading effect
  useEffect(() => {
    if (!projectId || !todoId) {
      setIsLoading(false);
      return;
    }

    const todoKey = `${projectId}-${todoId}`;

    if (loadingRef.current === todoKey) {
      return;
    }

    loadingRef.current = todoKey;

    // Load sequentially after the main todo: each route hits Postgres (token + work).
    // Parallel Promise.all(×4) spikes pool usage on Supavisor / small PG_POOL_MAX and causes
    // timeouts and misleading "Basecamp not connected" 401s when the token row read loses the race.
    void (async () => {
      try {
        await loadTodoDetails();
        // Secondary data after the viewer renders (do not block isLoading).
        void loadRelatedDocuments();
        // Defer connections: endpoint can run vector search + embedding; avoid spiking pool right after todo load.
        window.setTimeout(() => void loadConnections(), 2000);
        void loadBookmarkStatus();
      } finally {
        if (loadingRef.current === todoKey) {
          loadingRef.current = null;
        }
      }
    })();

    return () => {
      if (loadingRef.current === todoKey) {
        loadingRef.current = null;
      }
    };
  }, [todoId, projectId]);

  const loadTodoDetails = async (forceRefresh = false) => {
    if (!projectId) {
      setError('Project ID is required to load todo details');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setIsNotFound(false);

      const todoData = await basecampApi.getTodoById(projectId, todoId, forceRefresh);
      setTodo(todoData);

      if (onTodoLoad) {
        onTodoLoad(todoData);
      }

      console.log('[useBasecampTodoData] Todo loaded successfully:', todoData);
    } catch (err) {
      const notFound = err instanceof Error && err.message.startsWith('BASECAMP_TODO_NOT_FOUND');
      // A 404 here is expected/recoverable whenever this ID actually belongs to a BC5 step or
      // a todolist rather than a todo (see checks below) - only log unexpected failures loudly.
      if (!notFound && !isOfflineOrUnreachableFetchError(err)) {
        console.error('[useBasecampTodoData] Error loading todo:', err);
      }
      if (notFound && projectId) {
        try {
          const stepInfo = await basecampApi.getStepById(projectId, todoId);
          window.dispatchEvent(new CustomEvent('integration-view-change', {
            detail: {
              tabId: `basecamp-todo-${stepInfo.parentTodoId}`,
              title: 'Loading...',
              type: 'integration-basecamp-todo',
              integrationData: {
                integrationId: 'basecamp',
                projectId,
                todoId: stepInfo.parentTodoId,
                highlightStepId: todoId,
                ...(propProjectName ? { projectName: propProjectName } : {}),
              },
            },
          }));
          setIsLoading(false);
          return;
        } catch {
          // Not a step id either - keep checking below before giving up.
        }

        // Fast, cache-first check (DB only, no live Basecamp call): this ID is frequently a
        // todolist that was ingested into a FlowNotes folder (e.g. "escalated to folder"
        // structures), which 404s on the single-todo endpoint by design. Resolving this via the
        // cached/DB-backed search-todo lookup avoids falling through to the much slower
        // "guess and fetch every todo list in the project live" fallback on every visit.
        try {
          const searchResult = await basecampApi.searchTodoRecord(todoId);
          if (searchResult.found && searchResult.todo?.recordType === 'todolist') {
            window.dispatchEvent(new CustomEvent('integration-view-change', {
              detail: {
                tabId: `basecamp-todo-list-${todoId}`,
                title: searchResult.todo.title || 'Loading...',
                type: 'integration-basecamp-todo-list',
                integrationData: {
                  integrationId: 'basecamp',
                  projectId: searchResult.todo.projectId || projectId,
                  listId: todoId,
                  ...(propProjectName ? { projectName: propProjectName } : {}),
                },
              },
            }));
            setIsLoading(false);
            return;
          }
        } catch {
          // Cache lookup failed - fall through to the generic not-found/todolist-guess UI below.
        }
      }
      setIsNotFound(notFound);
      const msg = err instanceof Error ? err.message : 'Failed to load todo';
      const retryable = /restarting|database is briefly|503|pool is closed/i.test(msg);
      setError(notFound ? null : msg);
      if (retryable) {
        toast.message('API temporarily unavailable', {
          description: msg,
          duration: 6000,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadRelatedDocuments = async () => {
    try {
      setIsLoadingDocs(true);
      const docs = await basecampApi.getTodoRelatedDocuments(todoId, 100);
      setRelatedDocs(docs);
      console.log(`[useBasecampTodoData] Found ${docs.length} related documents`);
    } catch (err) {
      if (!isOfflineOrUnreachableFetchError(err)) {
        console.error('[useBasecampTodoData] Error loading related documents:', err);
      }
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const connectionsFetchKeyRef = useRef<string | null>(null);

  const loadConnections = async () => {
    const key = `${projectId}-${todoId}`;
    if (connectionsFetchKeyRef.current === key) return;
    connectionsFetchKeyRef.current = key;

    try {
      const { isApiDbCircuitOpen } = await import('@/lib/api/core');
      if (isApiDbCircuitOpen()) {
        connectionsFetchKeyRef.current = null;
        return;
      }
      setIsLoadingConnections(true);
      const data = await basecampApi.getTodoConnections(todoId, 10, 0.3);
      const semantic = data.semantic || [];
      const casual = data.casual || [];
      const todos = data.todos || [];
      setConnections({ semantic, casual, todos });
      const total = semantic.length + casual.length + todos.length;
      console.log(
        `[useBasecampTodoData] Found ${semantic.length} semantic + ${casual.length} casual + ${todos.length} related todos`
      );
      if (total === 0 && import.meta.env.DEV) {
        console.debug(
          '[useBasecampTodoData] Connections empty (API may have returned degraded empty on 502/503)'
        );
      }
    } catch (err) {
      connectionsFetchKeyRef.current = null;
      if (!isOfflineOrUnreachableFetchError(err)) {
        console.error('[useBasecampTodoData] Error loading connections:', err);
      }
    } finally {
      setIsLoadingConnections(false);
    }
  };

  const loadComments = useCallback(async () => {
    if (!todo || !projectId) return;

    try {
      setIsLoadingComments(true);
      setCommentsLoadFailed(false);
      const todoUrl = resolveBasecampTodoIngestUrlFromTodo(todo, projectId, todoId);
      const commentsData = await basecampApi.getComments(todoUrl);
      setComments(commentsData || []);
      setCommentsLoaded(true);
    } catch (err) {
      setCommentsLoadFailed(true);
      setCommentsLoaded(true);
      console.error('[useBasecampTodoData] Error loading comments:', err);
    } finally {
      setIsLoadingComments(false);
    }
  }, [todo, projectId, todoId]);

  // Optional: load comments on mount (disabled by default; viewer loads on COMMENTS panel open)
  useEffect(() => {
    if (
      !autoLoadComments ||
      !todo ||
      !projectId ||
      commentsLoaded ||
      isLoadingComments ||
      commentsLoadFailed
    ) {
      return;
    }
    void loadComments();
  }, [
    autoLoadComments,
    todo?.id,
    projectId,
    commentsLoaded,
    isLoadingComments,
    commentsLoadFailed,
    loadComments,
  ]);

  const bookmarkStatusKeyRef = useRef<string | null>(null);

  const loadBookmarkStatus = async () => {
    if (!projectId || !todoId) return;
    const key = `${projectId}-${todoId}`;
    if (bookmarkStatusKeyRef.current === key) return;
    bookmarkStatusKeyRef.current = key;

    try {
      const { isApiDbCircuitOpen } = await import('@/lib/api/core');
      if (isApiDbCircuitOpen()) {
        bookmarkStatusKeyRef.current = null;
        return;
      }
      const status = await basecampApi.getBookmarkStatus(projectId, todoId);
      setIsBookmarked(status.bookmarked);
    } catch (err) {
      bookmarkStatusKeyRef.current = null;
      if (!isOfflineOrUnreachableFetchError(err)) {
        console.warn('[useBasecampTodoData] Could not load bookmark status:', err);
      }
      setIsBookmarked(null);
    }
  };

  const toggleBookmark = async () => {
    if (!projectId || !todoId || isTogglingBookmark) return;

    const wasBookmarked = isBookmarked || false;
    const willBeBookmarked = !wasBookmarked;

    // OPTIMISTIC UPDATE: Update UI immediately
    setIsTogglingBookmark(true);
    setIsBookmarked(willBeBookmarked);

    // Optimistically update bookmarks widget
    window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
      detail: {
        todoId: todoId.toString(),
        projectId: projectId.toString(),
        projectName: projectName || 'Unknown Project',
        title: todo?.title || 'Untitled Todo',
        added: willBeBookmarked,
        optimistic: true // Signal this is an optimistic update
      }
    }));

    // Make API call in background
    try {
      if (wasBookmarked) {
        await basecampApi.removeBookmark(projectId, todoId);
      } else {
        await basecampApi.addBookmark(projectId, todoId);
      }

      // Success - show subtle toast
      toast.success(willBeBookmarked ? 'Bookmarked' : 'Unbookmarked', {
        description: 'Synced with Basecamp',
        duration: 2000,
        position: 'bottom-right'
      });

      // Trigger sync to refresh from server (gets accurate data)
      window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
        detail: {
          todoId: todoId.toString(),
          projectId: projectId.toString(),
          synced: true // Signal sync is complete
        }
      }));
    } catch (err) {
      // Rollback on error
      console.error('[useBasecampTodoData] Error toggling bookmark:', err);
      setIsBookmarked(wasBookmarked); // Rollback

      // Rollback bookmarks widget
      window.dispatchEvent(new CustomEvent('basecamp-bookmark-changed', {
        detail: {
          todoId: todoId.toString(),
          projectId: projectId.toString(),
          added: wasBookmarked, // Rollback to original state
          optimistic: true
        }
      }));

      toast.error('Failed to sync bookmark', {
        description: err instanceof Error ? err.message : 'Unknown error',
        duration: 3000
      });
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  const postComment = useCallback(async (content: string) => {
    if (!projectId || !todoId) {
      throw new Error('Project and todo ID are required to post a comment');
    }
    const trimmed = content.trim();
    if (!trimmed) return;

    try {
      setIsPostingComment(true);
      await basecampApi.postTodoComment(projectId, todoId, trimmed);
      await loadComments();
      toast.success('Comment posted to Basecamp');
    } catch (err) {
      toast.error('Could not post comment', {
        description: err instanceof Error ? err.message : 'Try again',
      });
      throw err;
    } finally {
      setIsPostingComment(false);
    }
  }, [projectId, todoId, loadComments]);

  return {
    // Data
    todo,
    comments,
    relatedDocs,
    connections,
    projectName,
    isBookmarked,
    // Loading states
    isLoading,
    isLoadingComments,
    commentsLoaded,
    commentsLoadFailed,
    isLoadingDocs,
    isLoadingConnections,
    isTogglingBookmark,
    isPostingComment,
    // Error state
    error,
    isNotFound,
    // Actions
    loadTodoDetails,
    loadComments,
    loadRelatedDocuments,
    loadConnections,
    loadBookmarkStatus,
    toggleBookmark,
    postComment,
  };
}

